import type { CashFlowType, ClientBalanceCategoryDto, DomicileDto } from '../api/generated/client';
import { PORTFOLIO_NUMBER_REGEX_CH, RESOURCE_KEY_LENGTH } from '../config/constants';
import { Domicile } from '../enums';
import type { ITalkingPointPin } from '../interfaces/client';
import { convertToUnixTimestampIfValidDateString } from './date-utils';

export const hasEntry = <T>(items?: T[]): items is T[] => !!items && items.length > 0;

export const concatClean = (...args: string[]) =>
    args
        .filter((a) => !!a)
        .join(' ')
        .replace(/[\s]{2,}/g, ' ')
        .trim();

export const getResourceKey = (enName: string) => (enName || '').trim().toLowerCase().replace(/\s/g, '_').substring(0, RESOURCE_KEY_LENGTH);

export const isEqualPins: (left: ITalkingPointPin, right: ITalkingPointPin) => boolean = (left, right) =>
    left.talkingPointId === right.talkingPointId && left.topicId === right.topicId;

export const getYwuShadowRoot = () => {
    const element = document.querySelector('ywu-app-element')
        ? document.querySelector('ywu-app-element')
        : document.querySelector('iap-client-data-app-element')?.shadowRoot?.querySelector('ywu-app-element');

    return element?.shadowRoot;
};

export type Order = 'asc' | 'desc';

const descendingComparator = <T, K extends keyof T>(a: T, b: T, orderBy: K) => {
    const [propA, propB] = convertToUnixTimestampIfValidDateString(a[orderBy], b[orderBy]);

    if (propB < propA) return -1;
    if (propB > propA) return 1;

    return 0;
};

/**
 * Creates a generic comparator which can be used for sorting an array of objects (type of T)
 * @param order The order by which the comparison should be done. Can be 'asc' | 'desc'.
 * @param orderBy The object key by which the comparison should be done.
 * @return Generic comparator function.
 * */
export const getComparator = <T, K extends keyof T>(order: Order, orderBy: K): ((a: T, b: T) => number) => {
    return order === 'desc' ? (a, b) => descendingComparator<T, K>(a, b, orderBy) : (a, b) => -1 * descendingComparator(a, b, orderBy);
};

export const getSignedAmountForType = (amount: number, type: CashFlowType) => (type === 'OUTFLOW' ? -Math.abs(amount) : amount);

export const formatRawPortfolioNumber = (rawPortfolioNumber: string) => {
    if (rawPortfolioNumber?.match(PORTFOLIO_NUMBER_REGEX_CH)) {
        const value = rawPortfolioNumber.replaceAll(/[^0-9]/g, '');

        return `${value.substring(0, 4)}.${value.substring(4, 8)}/${value.substring(8, 12)}`;
    }

    return ''; // TODO what to do about non-CH portfolios?
};

export const formatPortfolioNumber = (portfolioNumber?: string | null, businessRelationId?: string | null, portfolioIdentifier?: string | null): string => {
    const pn = portfolioNumber?.trim();
    const br = businessRelationId?.trim();

    if (pn && br) return formatRawPortfolioNumber(br.concat(pn));

    const composite = portfolioIdentifier?.trim();

    if (composite) {
        const digits = composite.replaceAll(/[^0-9]/g, '');

        if (digits.length === 12) return formatRawPortfolioNumber(digits.slice(4, 12) + digits.slice(0, 4));

        return formatRawPortfolioNumber(composite);
    }

    return '';
};

export const filterBalanceEntriesForCurrentYear = (categories: ClientBalanceCategoryDto[]): ClientBalanceCategoryDto[] =>
    categories.map((category) => {
        const currentYearRelevantEntries = category.entries.filter((entry) => entry.relevantForCurrentYear);

        return { ...category, entries: currentYearRelevantEntries };
    });

export const transformToTitleCase = (inputString?: string) =>
    inputString ? `${inputString.charAt(0).toUpperCase()}${inputString.slice(1).toLowerCase()}` : '';

export const roundTo2Decimals = (value: number): number => +value.toFixed(2);

export const combineTwoItemsIntoOne = (items: string[], separator: string = ' - '): string[] => {
    if (items.length === 0) {
        return [];
    }
    const evenItems = items.filter((_item, i) => i % 2 === 0);
    const oddItems = items.filter((_item, i) => i % 2 === 1);

    return evenItems.map((evenItem, i) => evenItem + (oddItems[i] ? separator + oddItems[i] : ''));
};

export const getProjectionYears = (years: number) => {
    if (years < 15) return 15;
    if (years > 30) return 30;

    return years;
};

export const getDefaultDomicileIsoAlpha2 = (availableDomiciles: DomicileDto[]) => {
    return availableDomiciles?.find((domicile) => domicile.isoAlpha2 === Domicile.CH) ? Domicile.CH : Domicile.GLOBAL;
};
